/**
 * 前端直传腾讯云开发云存储：通过 CloudBase JS SDK 的 uploadFile 上传，支持 onUploadProgress。
 * uploadFile 内部会正确设置 authorization / x-cos-security-token / x-cos-meta-fileid 等请求头，
 * 避免手动构造 PUT 请求时因请求头与签名不匹配导致的 403。
 */

import cloudbase from '@cloudbase/js-sdk';

export interface CloudBaseDirectUploadConfig {
  envId: string;
}

export interface CloudBaseDirectUploadOptions {
  /** 云存储中的路径，如 test/xxx.jpg */
  path: string;
  /** 上传进度回调，percent 0-100 */
  onProgress?: (e: { loaded: number; total: number; percent: number }) => void;
}

export interface CloudBaseDirectUploadResult {
  fileId: string;
  path: string;
}

const appCache = new Map<string, ReturnType<typeof cloudbase.init>>();

function getCloudBaseApp(envId: string) {
  let app = appCache.get(envId);
  if (!app) {
    app = cloudbase.init({ env: envId });
    appCache.set(envId, app);
  }
  return app;
}

/** 构建云存储 fileId，格式：cloud://envId.xxx/path */
export function buildFileId(envId: string, path: string): string {
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  return `cloud://${envId}/${normalized}`;
}

/**
 * 匿名登录（云开发未登录时，部分环境需先匿名登录才能调用存储等接口）
 */
export async function ensureAnonymousAuth(envId: string): Promise<void> {
  const app = getCloudBaseApp(envId);
  const auth = app.auth();
  if (!auth) {
    return;
  }
  const state = await auth.getLoginState();
  if (state) {
    return;
  }
  if (typeof auth.signInAnonymously !== 'function') {
    throw new Error('当前 CloudBase Auth 不支持 signInAnonymously，请确认 JS SDK 版本并在控制台开启匿名登录');
  }
  await auth.signInAnonymously();
}

type CloudbaseApp = ReturnType<typeof cloudbase.init>;

interface UploadFileParams {
  cloudPath: string;
  filePath: File;
  onUploadProgress?: (progress: { loaded: number; total: number }) => void;
}

interface UploadFileResult {
  fileID: string;
  download_url?: string;
}

/**
 * 前端上传文件到云存储，使用 SDK 内置的 uploadFile，由 SDK 负责设置所有必要请求头。
 * 支持 onProgress 实时进度回调。
 */
export async function uploadFileDirect(
  config: CloudBaseDirectUploadConfig,
  file: File,
  options: CloudBaseDirectUploadOptions
): Promise<CloudBaseDirectUploadResult> {
  const { envId } = config;
  const { path, onProgress } = options;

  const app = getCloudBaseApp(envId);

  // 通过 storage 模块的底层 uploadFile 方法上传，该方法会正确处理签名和请求头
  const storageModule = (app as CloudbaseApp & {
    uploadFile?: (params: UploadFileParams) => Promise<UploadFileResult>;
  });

  if (typeof storageModule.uploadFile !== 'function') {
    throw new Error('当前 CloudBase SDK 不支持 uploadFile，请确认版本');
  }

  let result: UploadFileResult;
  try {
    result = await storageModule.uploadFile({
      cloudPath: path,
      filePath: file,
      onUploadProgress: onProgress
        ? ({ loaded, total }: { loaded: number; total: number }) => {
            const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
            onProgress({ loaded, total, percent });
          }
        : undefined,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`上传失败: ${msg}`);
  }

  if (!result.fileID) {
    throw new Error('上传失败: 未返回 fileID');
  }

  return { fileId: result.fileID, path };
}
